/**
 * Build rate cards, packaging catalogues and product specs from simple CSV
 * templates, so founders can maintain them in a spreadsheet.
 */
import type { PackagingCatalogue, PackagingFormat, ProductSpec } from "./lever2.ts";
import { validateRateCard, type Band, type RateCard, type Service } from "./ratecard.ts";
import { gbpToPence, parseNumber } from "./units.ts";

export const RATE_CARD_COLUMNS = [
  "service_code", "service_name", "carrier", "volumetric_divisor",
  "band_code", "band_name", "max_length_cm", "max_width_cm", "max_height_cm", "max_weight_kg", "price_gbp",
] as const;

export const CATALOGUE_COLUMNS = [
  "code", "name", "kind", "inner_length_cm", "inner_width_cm", "inner_height_cm",
  "outer_length_cm", "outer_width_cm", "outer_height_cm", "tare_weight_kg", "unit_cost_gbp",
] as const;

export const PRODUCT_COLUMNS = ["sku", "length_cm", "width_cm", "height_cm", "weight_kg"] as const;

export interface ImportResult<T> {
  value: T | null;
  problems: string[];
}

type Row = Record<string, unknown>;

/** Header lookup that ignores case and surrounding spaces. */
function getter(row: Row) {
  const keys = new Map(Object.keys(row).map((k) => [k.trim().toLowerCase(), k]));
  return (col: string): unknown => {
    const k = keys.get(col);
    return k === undefined ? undefined : row[k];
  };
}

function missingColumns(rows: Row[], cols: readonly string[]): string[] {
  const first = rows[0];
  if (!first) return [];
  const have = new Set(Object.keys(first).map((k) => k.trim().toLowerCase()));
  return cols.filter((c) => !have.has(c));
}

function sorted3(a: number, b: number, c: number): [number, number, number] {
  const s = [a, b, c].sort((x, y) => y - x);
  return [s[0]!, s[1]!, s[2]!];
}

export function rateCardFromRows(
  rows: Row[],
  meta: { id: string; version: string; effectiveFrom: string; source: string },
): ImportResult<RateCard> {
  const problems: string[] = [];
  if (!/^[a-z0-9][a-z0-9-]*$/.test(meta.id)) problems.push("Rate card ID: lower-case letters, digits and hyphens only");
  if (!meta.version.trim()) problems.push("Version is required");
  if (!meta.source.trim()) problems.push("Source is required: where the prices came from");
  if (rows.length === 0) problems.push("The file has no rows");
  // service_name, carrier, band_name and volumetric_divisor are optional
  const missing = missingColumns(rows, ["service_code", "band_code", "max_length_cm", "max_width_cm", "max_height_cm", "max_weight_kg", "price_gbp"]);
  if (missing.length) problems.push(`Missing columns: ${missing.join(", ")}`);
  if (problems.length) return { value: null, problems };

  const services = new Map<string, Service>();
  rows.forEach((row, i) => {
    const g = getter(row);
    const line = i + 2;
    const code = String(g("service_code") ?? "").trim();
    const bandCode = String(g("band_code") ?? "").trim();
    if (!code || !bandCode) {
      problems.push(`Line ${line}: service_code and band_code are required`);
      return;
    }
    const n = (col: string) => {
      const v = parseNumber(g(col));
      if (v === null || v <= 0) problems.push(`Line ${line}: ${col} must be a number above 0`);
      return v ?? 0;
    };
    const [l, w, h] = sorted3(n("max_length_cm"), n("max_width_cm"), n("max_height_cm"));
    const price = parseNumber(g("price_gbp"));
    if (price === null || price < 0) problems.push(`Line ${line}: price_gbp must be a number`);
    const divisorRaw = g("volumetric_divisor");
    const divisor = divisorRaw === undefined || String(divisorRaw).trim() === "" ? null : parseNumber(divisorRaw);
    if (divisorRaw !== undefined && String(divisorRaw).trim() !== "" && (divisor === null || divisor <= 0)) {
      problems.push(`Line ${line}: volumetric_divisor must be blank or a number above 0`);
    }

    let svc = services.get(code);
    if (!svc) {
      svc = {
        code,
        name: String(g("service_name") ?? "").trim() || code,
        carrier: String(g("carrier") ?? "").trim(),
        volumetricDivisor: divisor,
        bands: [],
      };
      services.set(code, svc);
    } else if (svc.volumetricDivisor !== divisor) {
      problems.push(`Line ${line}: every band of ${code} must have the same volumetric_divisor`);
    }
    if (svc.bands.some((b) => b.code === bandCode)) problems.push(`Line ${line}: band ${bandCode} appears twice in ${code}`);
    const band: Band = {
      code: bandCode,
      name: String(g("band_name") ?? "").trim() || bandCode,
      maxLengthCm: l,
      maxWidthCm: w,
      maxHeightCm: h,
      maxWeightKg: n("max_weight_kg"),
      pricePence: gbpToPence(price ?? 0),
    };
    svc.bands.push(band);
  });

  const card: RateCard = { id: meta.id, version: meta.version.trim(), effectiveFrom: meta.effectiveFrom, source: meta.source.trim(), services: [...services.values()] };
  problems.push(...validateRateCard(card));
  return problems.length ? { value: null, problems } : { value: card, problems };
}

export function catalogueFromRows(rows: Row[], meta: { id: string; version: string }): ImportResult<PackagingCatalogue> {
  const problems: string[] = [];
  if (!/^[a-z0-9][a-z0-9-]*$/.test(meta.id)) problems.push("Catalogue ID: lower-case letters, digits and hyphens only");
  if (!meta.version.trim()) problems.push("Version is required");
  if (rows.length === 0) problems.push("The file has no rows");
  const missing = missingColumns(rows, CATALOGUE_COLUMNS);
  if (missing.length) problems.push(`Missing columns: ${missing.join(", ")}`);
  if (problems.length) return { value: null, problems };

  const formats: PackagingFormat[] = [];
  rows.forEach((row, i) => {
    const g = getter(row);
    const line = i + 2;
    const n = (col: string, allowZero = false) => {
      const v = parseNumber(g(col));
      if (v === null || v < 0 || (!allowZero && v === 0)) problems.push(`Line ${line}: ${col} must be a number${allowZero ? "" : " above 0"}`);
      return v ?? 0;
    };
    const code = String(g("code") ?? "").trim();
    if (!code) problems.push(`Line ${line}: code is required`);
    if (formats.some((f) => f.code === code)) problems.push(`Line ${line}: code ${code} appears twice`);
    const kind = String(g("kind") ?? "").trim().toLowerCase();
    if (kind !== "box" && kind !== "mailer" && kind !== "envelope") problems.push(`Line ${line}: kind must be box, mailer or envelope`);
    const inner = sorted3(n("inner_length_cm"), n("inner_width_cm"), n("inner_height_cm"));
    const outer = sorted3(n("outer_length_cm"), n("outer_width_cm"), n("outer_height_cm"));
    if (inner.some((v, k) => v > outer[k]!)) problems.push(`Line ${line}: inner size cannot be bigger than outer size`);
    formats.push({
      code,
      name: String(g("name") ?? "").trim() || code,
      kind: kind as PackagingFormat["kind"],
      innerDims: { lengthCm: inner[0], widthCm: inner[1], heightCm: inner[2] },
      outerDims: { lengthCm: outer[0], widthCm: outer[1], heightCm: outer[2] },
      tareWeightKg: n("tare_weight_kg", true),
      unitCostPence: gbpToPence(n("unit_cost_gbp", true)),
    });
  });
  return problems.length ? { value: null, problems } : { value: { id: meta.id, version: meta.version.trim(), formats }, problems };
}

export function productSpecsFromRows(rows: Row[]): ImportResult<ProductSpec[]> {
  const problems: string[] = [];
  if (rows.length === 0) problems.push("The file has no rows");
  const missing = missingColumns(rows, PRODUCT_COLUMNS);
  if (missing.length) problems.push(`Missing columns: ${missing.join(", ")}`);
  if (problems.length) return { value: null, problems };
  const seen = new Set<string>();
  const specs: ProductSpec[] = [];
  rows.forEach((row, i) => {
    const g = getter(row);
    const line = i + 2;
    const sku = String(g("sku") ?? "").trim();
    if (!sku) {
      problems.push(`Line ${line}: sku is required`);
      return;
    }
    if (seen.has(sku)) problems.push(`Line ${line}: sku ${sku} appears twice`);
    seen.add(sku);
    const n = (col: string) => {
      const v = parseNumber(g(col));
      if (v === null || v <= 0) problems.push(`Line ${line}: ${col} must be a number above 0`);
      return v ?? 0;
    };
    specs.push({ sku, dims: { lengthCm: n("length_cm"), widthCm: n("width_cm"), heightCm: n("height_cm") }, weightKg: n("weight_kg") });
  });
  return problems.length ? { value: null, problems } : { value: specs, problems };
}

/** A CSV template with the right headers and one example row. */
export function csvTemplate(kind: "rate-card" | "catalogue" | "products"): string {
  if (kind === "rate-card") {
    return [
      RATE_CARD_COLUMNS.join(","),
      "POST48,Example post 48 hour,Example Carrier,,SP,Small parcel,45,35,16,2,3.49",
      "COURIER48,Example courier 48 hour,Example Courier,5000,C5,Up to 5kg,100,60,60,5,6.50",
    ].join("\n") + "\n";
  }
  if (kind === "catalogue") {
    return [CATALOGUE_COLUMNS.join(","), "MAIL-S,Small mailer,mailer,32,24,6,33,25,7,0.05,0.18"].join("\n") + "\n";
  }
  return [PRODUCT_COLUMNS.join(","), "TEE,25,18,3,0.25"].join("\n") + "\n";
}
